/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { Skeleton } from 'antd'
import React from 'react'

/** Rows per section, mirroring the real form's 2- and 3-column rhythm. */
const SECTIONS = [
  { title: 'ORGANIZATION INFORMATION', rows: [2, 1] },
  { title: 'CONTACT INFORMATION', rows: [2, 3, 2, 1] },
  { title: 'BUSINESS INFORMATION', rows: [2, 1] },
]

/**
 * Placeholder shown while the existing-application check is in flight.
 *
 * Deliberately shaped like the form rather than a generic spinner: the check
 * usually resolves to "no application", so matching the layout underneath means
 * the swap does not visibly reflow.
 */
const SportsPayApplicationSkeleton: React.FC = () => (
  <div css={wrapperStyle} aria-busy aria-label='Loading application'>
    {SECTIONS.map((section) => (
      <div key={section.title} css={sectionStyle}>
        <Skeleton.Input active size='small' css={headingStyle} />

        {section.rows.map((columns, index) => (
          <div
            // Rows are positional and never reorder, so the index is a stable
            // identity here.
            key={`${section.title}-${index}`}
            css={rowStyle(columns)}
          >
            {Array.from({ length: columns }, (_, column) => (
              <Skeleton.Input
                key={column}
                active
                block
                css={fieldStyle}
                size='default'
              />
            ))}
          </div>
        ))}
      </div>
    ))}
  </div>
)

export default SportsPayApplicationSkeleton

/* -------------------------------------------------------------------------- */
/*                                   Styles                                   */
/* -------------------------------------------------------------------------- */

/* No padding — matches the form and status views so all three align. */
const wrapperStyle = css`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const sectionStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

/**
 * antd sizes its skeleton inputs intrinsically and enforces a min-width, which
 * overflows a narrow grid column. Both are overridden so the blocks track the
 * column they sit in.
 */
const fieldStyle = css`
  &.ant-skeleton {
    width: 100%;
    min-width: 0;
  }
  .ant-skeleton-input {
    width: 100% !important;
    min-width: 0 !important;
    height: 40px;
    border-radius: 8px;
    background: ${colors.SECONDARY_200};
  }
`

const headingStyle = css`
  .ant-skeleton-input {
    width: 180px !important;
    min-width: 0 !important;
    height: 14px;
    border-radius: 4px;
    background: ${colors.SECONDARY_200};
  }
`

const rowStyle = (columns: number) => css`
  display: grid;
  grid-template-columns: repeat(${columns}, minmax(0, 1fr));
  gap: 16px;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`
